import { Component } from '@theme/component';

const LIGHTBOX_STYLES = `
  .rte-zoom-trigger {
    display: inline-block;
    max-width: 100%;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    line-height: 0;
    cursor: zoom-in;
  }

  .rte-zoom-trigger:focus-visible {
    outline: 2px solid currentcolor;
    outline-offset: 2px;
  }

  .rte-lightbox {
    width: 100%;
    max-width: 100vw;
    height: 100%;
    max-height: 100vh;
    padding: 0;
    border: 0;
    background: transparent;
    overflow: hidden;
  }

  .rte-lightbox::backdrop {
    background: rgb(0 0 0 / 0.85);
  }

  .rte-lightbox__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    cursor: zoom-out;
  }

  .rte-lightbox__close {
    position: fixed;
    inset-block-start: 1rem;
    inset-inline-end: 1rem;
    z-index: 1;
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: var(--color-background, #fff);
    color: var(--color-foreground, #000);
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
  }
`;

/** @type {{ dialog: HTMLDialogElement, image: HTMLImageElement } | null} */
let lightbox = null;

/** @type {{ element: HTMLElement, previousOverflow: string } | null} */
let scrollLock = null;

/**
 * Finds the element that actually scrolls around a node. The theme scrolls an
 * inner wrapper rather than the document, so locking the document would be a no-op.
 * @param {Element} node
 * @returns {HTMLElement}
 */
function getScrollContainer(node) {
  for (let element = node.parentElement; element; element = element.parentElement) {
    const { overflowY } = getComputedStyle(element);
    if (/(auto|scroll)/.test(overflowY) && element.scrollHeight > element.clientHeight) return element;
  }

  return /** @type {HTMLElement} */ (document.scrollingElement ?? document.documentElement);
}

/**
 * Stops the page behind the lightbox from scrolling while it is open.
 * @param {Element} node - A node inside the scroll container to lock.
 */
function lockScroll(node) {
  const element = getScrollContainer(node);
  scrollLock = { element, previousOverflow: element.style.overflowY };
  element.style.overflowY = 'hidden';
}

/** Restores whatever overflow the scroll container had before it was locked. */
function unlockScroll() {
  if (!scrollLock) return;

  const { element, previousOverflow } = scrollLock;
  if (previousOverflow) element.style.overflowY = previousOverflow;
  else element.style.removeProperty('overflow-y');

  scrollLock = null;
}

/**
 * Builds the lightbox once and reuses it for every RTE image on the page.
 * @returns {{ dialog: HTMLDialogElement, image: HTMLImageElement }}
 */
function getLightbox() {
  if (lightbox) return lightbox;

  const styles = document.createElement('style');
  styles.textContent = LIGHTBOX_STYLES;
  document.head.append(styles);

  const dialog = document.createElement('dialog');
  dialog.className = 'rte-lightbox';

  const image = document.createElement('img');
  image.className = 'rte-lightbox__image';
  image.alt = '';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'rte-lightbox__close';
  close.setAttribute('aria-label', 'Close');
  close.textContent = '×';

  dialog.append(close, image);
  document.body.append(dialog);

  // Clicking anywhere dismisses, matching the zoom-out cursor. Escape is handled natively.
  dialog.addEventListener('click', () => dialog.close());

  // showModal blocks interaction but not scrolling, so the page is locked while open.
  dialog.addEventListener('close', () => {
    unlockScroll();
    image.removeAttribute('src');
  });

  lightbox = { dialog, image };
  return lightbox;
}

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.querySelectorAll('img').forEach(this.#makeZoomable);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Wraps an image in a button so it can be opened full size by click or keyboard.
   * Images that are already links keep their existing behaviour.
   * @param {HTMLImageElement} image
   */
  #makeZoomable(image) {
    if (image.closest('a') || image.closest('.rte-zoom-trigger')) return;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'rte-zoom-trigger';
    trigger.setAttribute('aria-label', image.alt ? `Enlarge image: ${image.alt}` : 'Enlarge image');

    const parent = image.parentNode;
    if (!parent) return;

    parent.insertBefore(trigger, image);
    trigger.appendChild(image);

    trigger.addEventListener('click', () => {
      const { dialog, image: target } = getLightbox();

      target.src = image.currentSrc || image.src;
      target.alt = image.alt;
      dialog.setAttribute('aria-label', image.alt ? `Enlarged image: ${image.alt}` : 'Enlarged image');

      lockScroll(image);
      dialog.showModal();
    });
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
